import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  FaTimes,
  FaSave,
  FaCalendarAlt,
  FaUser,
  FaThList,
  FaFolder,
  FaTags,
  FaFileAlt,
  FaTrashAlt,
} from 'react-icons/fa';
import {
  TechButton,
  TechButtonGhost,
  TechButtonDanger,
  TechInput,
  TechSelect,
  TechTextarea,
  TechChip,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';
import { CATEGORY_META } from './data';

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideUp = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.985); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  background:
    radial-gradient(ellipse at center, rgba(0, 180, 216, 0.08), transparent 60%),
    rgba(11, 19, 32, 0.78);
  backdrop-filter: blur(6px);
  display: grid;
  place-items: center;
  padding: 1.5rem 1rem;
  animation: ${fadeIn} 0.18s ease;
`;

const Modal = styled.form`
  position: relative;
  width: 100%;
  max-width: 760px;
  max-height: 90vh;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.012), transparent 35%),
    var(--color-tertiary);
  border: 1px solid ${borderSoft};
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: ${slideUp} 0.22s ease;

  &::before, &::after {
    content: '';
    position: absolute;
    width: 14px;
    height: 14px;
    border-color: ${accent};
    border-style: solid;
    border-width: 0;
    pointer-events: none;
    z-index: 2;
  }
  &::before { top: -1px; left: -1px; border-top-width: 1px; border-left-width: 1px; }
  &::after { bottom: -1px; right: -1px; border-bottom-width: 1px; border-right-width: 1px; }
`;

const Header = styled.header`
  position: relative;
  padding: 1.1rem 1.4rem;
  border-bottom: 1px solid ${borderSoft};
  background:
    linear-gradient(135deg, rgba(0, 180, 216, 0.08), transparent 55%),
    rgba(36, 52, 77, 0.7);
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 0.85rem;
  right: 0.85rem;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid ${borderSoft};
  background: rgba(11, 19, 32, 0.5);
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: color 0.18s ease, border-color 0.18s ease;

  &:hover { color: var(--alert-error); border-color: rgba(239, 71, 111, 0.4); }
`;

const Title = styled.h2`
  margin: 0 0 0.35rem;
  color: var(--text-primary);
  font-size: 1.15rem;
  letter-spacing: -0.005em;
  display: flex;
  align-items: center;
  gap: 0.55rem;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${accent};
    box-shadow: 0 0 0 3px rgba(0, 180, 216, 0.14);
  }
`;

const Subtitle = styled.p`
  margin: 0;
  color: var(--text-disabled);
  font-size: 0.86rem;
  font-family: ${mono};
  letter-spacing: 0.04em;
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.1rem 1.4rem;

  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.08); border-radius: 8px; }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 0.95rem;

  .label {
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--text-disabled);
    display: flex;
    align-items: center;
    gap: 0.4rem;

    svg { color: ${accent}; font-size: 0.75rem; }
  }

  .help {
    font-size: 0.78rem;
    color: var(--text-disabled);
    margin: 0;
  }

  .err {
    font-family: ${mono};
    font-size: 0.65rem;
    color: var(--alert-error);
    letter-spacing: 0.04em;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: ${({ cols }) => `repeat(${cols || 2}, 1fr)`};
  gap: 0.85rem;

  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const TagsInputWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 6px;
  padding: 0.4rem 0.5rem;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;

  &:focus-within {
    border-color: rgba(0, 180, 216, 0.45);
    box-shadow: 0 0 0 3px rgba(0, 180, 216, 0.1);
  }

  input {
    flex: 1;
    min-width: 120px;
    border: none;
    outline: none;
    background: transparent;
    color: var(--text-primary);
    font-size: 0.9rem;
    font-family: inherit;
    padding: 0.3rem 0.2rem;

    &::placeholder { color: var(--text-disabled); }
  }
`;

const TagPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background: rgba(0, 180, 216, 0.08);
  border: 1px solid rgba(0, 180, 216, 0.28);
  color: #cfe9f4;
  border-radius: 3px;
  padding: 2px 6px 2px 8px;
  font-family: ${mono};
  font-size: 0.7rem;

  &::before { content: '#'; color: ${accent}; opacity: 0.85; }

  button {
    background: transparent;
    border: none;
    color: rgba(207, 233, 244, 0.7);
    cursor: pointer;
    padding: 0;
    margin-left: 2px;
    display: inline-flex;
    align-items: center;
    font-size: 0.7rem;

    &:hover { color: var(--alert-error); }
  }
`;

const Footer = styled.footer`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.55rem;
  padding: 0.85rem 1.4rem;
  border-top: 1px solid ${borderSoft};
  background: ${panelDeep};
  flex-wrap: wrap;
`;

const TYPES = ['Informe', 'Estudio', 'Dashboard', 'Encuesta'];

const todayIso = () => new Date().toISOString().slice(0, 10);

const initialForm = (item) => ({
  title: item?.title || '',
  type: item?.type || 'Informe',
  category: item?.category || 'electoral',
  author: item?.author || '',
  date: item?.date || todayIso(),
  pages: item?.pages || '',
  excerpt: item?.excerpt || '',
  body: item?.body || '',
});

const ResearchEditor = ({ open, item, onClose, onSave, onDelete }) => {
  const [form, setForm] = useState(initialForm(item));
  const [tags, setTags] = useState(item?.tags || []);
  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setForm(initialForm(item));
    setTags(item?.tags || []);
    setTagInput('');
    setErrors({});
  }, [open, item]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addTag = () => {
    const t = tagInput.trim().replace(/^#/, '').toLowerCase();
    if (!t) return;
    if (tags.includes(t)) { setTagInput(''); return; }
    setTags([...tags, t]);
    setTagInput('');
  };

  const removeTag = (t) => setTags(tags.filter((x) => x !== t));

  const onTagKey = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    } else if (e.key === 'Backspace' && !tagInput && tags.length) {
      setTags(tags.slice(0, -1));
    }
  };

  const validate = () => {
    const errs = {};
    if (!form.title.trim()) errs.title = 'Requerido';
    if (!form.author.trim()) errs.author = 'Requerido';
    if (!form.excerpt.trim()) errs.excerpt = 'Requerido';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const payload = {
      ...item,
      ...form,
      pages: form.pages ? Number(form.pages) : undefined,
      tags,
    };
    onSave?.(payload);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  const isEdit = Boolean(item?.id);

  return (
    <Backdrop onClick={handleBackdropClick} role="dialog" aria-modal="true">
      <Modal onSubmit={handleSubmit}>
        <Header>
          <Title>{isEdit ? 'Editar investigación' : 'Nueva investigación'}</Title>
          <Subtitle>RES://NEW · borrador local</Subtitle>
          <CloseBtn type="button" onClick={onClose} aria-label="Cerrar">
            <FaTimes />
          </CloseBtn>
        </Header>

        <Body>
          <Field>
            <span className="label"><FaFileAlt /> Título *</span>
            <TechInput
              autoFocus
              placeholder="Ej: Análisis de intención de voto en barrio Don Bosco"
              value={form.title}
              onChange={update('title')}
            />
            {errors.title && <span className="err">{errors.title}</span>}
          </Field>

          <Row cols={2}>
            <Field>
              <span className="label"><FaThList /> Tipo</span>
              <TechSelect value={form.type} onChange={update('type')}>
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </TechSelect>
            </Field>
            <Field>
              <span className="label"><FaFolder /> Categoría</span>
              <TechSelect value={form.category} onChange={update('category')}>
                {Object.entries(CATEGORY_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </TechSelect>
            </Field>
          </Row>

          <Row cols={3}>
            <Field>
              <span className="label"><FaUser /> Autor *</span>
              <TechInput
                placeholder="Equipo o persona"
                value={form.author}
                onChange={update('author')}
              />
              {errors.author && <span className="err">{errors.author}</span>}
            </Field>
            <Field>
              <span className="label"><FaCalendarAlt /> Fecha</span>
              <TechInput type="date" value={form.date} onChange={update('date')} />
            </Field>
            <Field>
              <span className="label">Páginas</span>
              <TechInput
                type="number"
                min="0"
                placeholder="Ej: 12"
                value={form.pages}
                onChange={update('pages')}
              />
            </Field>
          </Row>

          <Field>
            <span className="label"><FaTags /> Tags</span>
            <TagsInputWrap onClick={(e) => e.currentTarget.querySelector('input')?.focus()}>
              {tags.map((t) => (
                <TagPill key={t}>
                  {t}
                  <button type="button" onClick={() => removeTag(t)} aria-label={`Quitar ${t}`}>
                    <FaTimes />
                  </button>
                </TagPill>
              ))}
              <input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={onTagKey}
                onBlur={addTag}
                placeholder={tags.length === 0 ? 'Agregar tag y enter…' : ''}
              />
            </TagsInputWrap>
            <span className="help">Enter o coma para confirmar. Backspace borra el último.</span>
          </Field>

          <Field>
            <span className="label">Resumen breve *</span>
            <TechTextarea
              rows={3}
              placeholder="Descripción corta que aparece en la card"
              value={form.excerpt}
              onChange={update('excerpt')}
            />
            {errors.excerpt && <span className="err">{errors.excerpt}</span>}
          </Field>

          <Field>
            <span className="label">Cuerpo (markdown)</span>
            <TechTextarea
              rows={8}
              placeholder={'## Hallazgos\n\n- Hallazgo 1\n- Hallazgo 2\n\n## Recomendación\n\nTexto…'}
              value={form.body}
              onChange={update('body')}
              style={{ fontFamily: mono, fontSize: '0.86rem' }}
            />
            <span className="help">Soporta encabezados, listas, tablas y enlaces markdown.</span>
          </Field>
        </Body>

        <Footer>
          {isEdit && onDelete ? (
            <TechButtonDanger
              type="button"
              onClick={() => {
                if (window.confirm('¿Eliminar esta investigación? Esta acción no se puede deshacer.')) {
                  onDelete(item.id);
                }
              }}
            >
              <FaTrashAlt /> Eliminar
            </TechButtonDanger>
          ) : <span />}

          <div style={{ display: 'flex', gap: 6 }}>
            <TechButtonGhost type="button" onClick={onClose}>Cancelar</TechButtonGhost>
            <TechButton type="submit">
              <FaSave /> {isEdit ? 'Guardar cambios' : 'Crear investigación'}
            </TechButton>
          </div>
        </Footer>
      </Modal>
    </Backdrop>
  );
};

export default ResearchEditor;
